/**
 * BLOQUE 5 — Validación Cruzada enriquecida.
 * Se activa automáticamente cuando todas las metodologías seleccionadas tienen
 * reporte. Botón único "Ejecutar Validación Cruzada" lanza el job.
 */
import { useEffect, useRef, useState } from "react";
import { api } from "../../../lib/api";
import {
  Loader2, CheckCircle, AlertCircle, Download, Activity,
} from "lucide-react";

const AGREEMENT_COLOR = { Alto: "#00C853", Medio: "#FB8C00", Bajo: "#90A4AE" };

export default function CrossValEnriched({ caseId, selected, reports, reload }) {
  const completed = Object.keys(reports || {}).length;
  const total = selected.length;
  const allDone = total >= 2 && completed >= total;
  const cross = reports?.__crossval_v2 || null;

  const [jobId, setJobId] = useState(null);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState("");
  const pollRef = useRef(null);

  async function start() {
    setError("");
    setProgress({ status: "processing", progress_pct: 0, current_step: "Iniciando..." });
    try {
      const r = await api.post(`/investigations/${caseId}/run-analysis`, { kind: "crossval" });
      setJobId(r.data.job_id);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
      setProgress(null);
    }
  }

  useEffect(() => {
    if (!jobId) return;
    let mounted = true;
    async function tick() {
      try {
        const r = await api.get(`/investigations/${caseId}/analysis-status/${jobId}`);
        if (!mounted) return;
        setProgress(r.data);
        if (r.data.status === "completed") {
          clearInterval(pollRef.current);
          await reload();
          setJobId(null);
        } else if (r.data.status === "failed") {
          setError(r.data.error || "El análisis falló");
          clearInterval(pollRef.current);
          setJobId(null);
        }
      } catch (e) {
        setError(e?.message);
        clearInterval(pollRef.current);
        setJobId(null);
      }
    }
    tick();
    pollRef.current = setInterval(tick, 4000);
    return () => {
      mounted = false;
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [jobId, caseId, reload]);

  async function downloadDocx() {
    try {
      const r = await api.post(
        `/investigations/${caseId}/crossval-v2/docx`, {}, { responseType: "blob" },
      );
      triggerDownload(r.data, `${caseId}_crossval_integrado.docx`,
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document");
    } catch (e) { alert(e?.response?.data?.detail || e.message); }
  }
  async function downloadPdf() {
    try {
      const r = await api.post(
        `/investigations/${caseId}/crossval-v2/pdf`, {}, { responseType: "blob" },
      );
      triggerDownload(r.data, `${caseId}_crossval_integrado.pdf`, "application/pdf");
    } catch (e) { alert(e?.response?.data?.detail || e.message); }
  }

  function triggerDownload(blob, name, type) {
    const url = URL.createObjectURL(new Blob([blob], { type }));
    const a = document.createElement("a");
    a.href = url; a.download = name;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 800);
  }

  const result = cross?.result || {};
  const matrix = result.convergence_matrix || [];
  const blind = result.blind_spots || [];
  const integrated = result.integrated_conclusion || {};
  const actions = result.action_items || [];

  return (
    <div className="mt-3" data-testid="psc-crossval-v2">
      {/* Banner de activación automática */}
      {allDone && !cross && !progress && !error && (
        <div className="card" style={{
          background: "rgba(0,200,83,0.10)", borderColor: "#00C853",
        }} data-testid="psc-crossval-banner">
          <div className="flex items-center justify-between">
            <div>
              <CheckCircle size={14} style={{ display: "inline", color: "#00C853" }}/>
              <b className="ml-1">Todas las metodologías completadas ({completed}/{total}).</b>
              <div className="text-xs text-dim">
                Validación Cruzada disponible — iniciar comparativa automática.
              </div>
            </div>
            <button className="btn btn-primary" onClick={start} data-testid="psc-crossval-start">
              <Activity size={11}/> Ejecutar Validación Cruzada
            </button>
          </div>
        </div>
      )}

      {progress && (
        <div className="card mt-2" data-testid="psc-crossval-progress">
          <div className="flex items-center gap-2 mb-1">
            <Loader2 size={14} className="spin"/>
            <b>Integrando metodologías...</b>
            <span className="text-xs text-dim">{progress.progress_pct}%</span>
          </div>
          <div style={{ background: "var(--bg-2)", borderRadius: 4, height: 6 }}>
            <div style={{
              width: `${progress.progress_pct || 0}%`,
              height: "100%", background: "var(--accent)",
              transition: "width 0.4s ease",
            }}/>
          </div>
          <div className="text-xs text-dim mt-1">{progress.current_step}</div>
        </div>
      )}

      {error && (
        <div className="card mt-2" style={{ background: "rgba(213,0,0,0.10)", borderColor: "#D50000" }}>
          <AlertCircle size={12} style={{ display: "inline" }}/> {error}
          <button className="btn btn-sm ml-2" onClick={start}>Reintentar</button>
        </div>
      )}

      {cross && (
        <div className="space-y-3 mt-2">
          {/* SECCIÓN A — Matriz de Convergencia */}
          {matrix.length > 0 && (
            <div className="card" data-testid="psc-crossval-matrix">
              <div className="card-title">A · Matriz de Convergencia</div>
              <table style={{ width: "100%", fontSize: 11, borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: "var(--bg-2)" }}>
                    <th style={{ padding: 6, textAlign: "left" }}>Causa / Factor</th>
                    <th style={{ padding: 6 }}>Metodologías</th>
                    <th style={{ padding: 6 }}>Nivel de Acuerdo</th>
                  </tr>
                </thead>
                <tbody>
                  {matrix.map((row, i) => {
                    const bg = row.agreement_level === "Alto" ? "rgba(0,200,83,0.10)"
                              : row.agreement_level === "Medio" ? "rgba(251,140,0,0.10)"
                              : "var(--bg-2)";
                    return (
                      <tr key={`m-${i}`} style={{ background: bg, borderTop: "1px solid var(--border-2)" }}>
                        <td style={{ padding: 6 }}>{row.cause}</td>
                        <td style={{ padding: 6 }}>{(row.methodologies || []).join(", ")}</td>
                        <td style={{ padding: 6 }}>
                          <span style={{
                            background: AGREEMENT_COLOR[row.agreement_level] || "#90A4AE",
                            color: "#FFF", padding: "2px 8px", borderRadius: 10,
                            fontSize: 10, fontWeight: 700,
                          }}>{row.agreement_level || "—"}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* SECCIÓN B — Conclusión Integrada */}
          {integrated.narrative && (
            <div className="card" style={{
              background: "rgba(21,101,192,0.08)", borderColor: "#1565C0",
            }} data-testid="psc-crossval-integrated">
              <div className="card-title">B · Documento de Conclusión Integrado</div>
              <p className="text-sm">{integrated.narrative}</p>
              {(integrated.validated_root_causes || []).length > 0 && (
                <div className="mt-2">
                  <b className="text-sm">Causas raíz validadas:</b>
                  <ul style={{ paddingLeft: 18 }}>
                    {integrated.validated_root_causes.map((c, i) => (
                      <li key={`vrc-${i}`} className="text-sm">
                        {c.cause} <span className="text-xs" style={{
                          background: "#1565C0", color: "#FFF",
                          padding: "1px 6px", borderRadius: 8, marginLeft: 4,
                        }}>{c.confidence_pct || "—"}% confianza</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {integrated.by_dimension && (
                <div className="grid mt-2" style={{ gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  {Object.entries(integrated.by_dimension).map(([dim, items]) => (
                    (items || []).length > 0 && (
                      <div key={`dim-${dim}`} className="card" style={{ background: "var(--bg-2)" }}>
                        <b style={{ textTransform: "capitalize" }}>{dim}</b>
                        <ul style={{ paddingLeft: 14, fontSize: 12 }}>
                          {(items || []).map((it, i) => <li key={`d-${dim}-${i}`}>{it}</li>)}
                        </ul>
                      </div>
                    )
                  ))}
                </div>
              )}
            </div>
          )}

          {/* SECCIÓN C — Áreas Ciegas y Oportunidades */}
          {(blind.length > 0 || (integrated.opportunity_areas || []).length > 0) && (
            <div className="card" data-testid="psc-crossval-blind">
              <div className="card-title">C · Áreas Ciegas y Oportunidades 360°</div>
              {blind.map((b, i) => (
                <div key={`bl-${i}`} className="mt-1 text-sm">
                  <b>{b.methodology}:</b>
                  <ul style={{ paddingLeft: 18 }}>
                    {(b.unique_findings || []).map((f, j) => (
                      <li key={`uf-${i}-${j}`}>{f}</li>
                    ))}
                  </ul>
                </div>
              ))}
              {(integrated.opportunity_areas || []).length > 0 && (
                <div className="mt-2">
                  <b className="text-sm">Áreas para investigación adicional:</b>
                  <ul style={{ paddingLeft: 18 }}>
                    {integrated.opportunity_areas.map((o, i) => (
                      <li key={`opp-${i}`} className="text-sm">{o}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* SECCIÓN D — Acciones Integradas */}
          {actions.length > 0 && (
            <div className="card" data-testid="psc-crossval-actions">
              <div className="card-title">D · Acciones Correctivas Integradas</div>
              <table style={{ width: "100%", fontSize: 11 }}>
                <thead>
                  <tr style={{ background: "var(--bg-2)" }}>
                    <th style={{ padding: 6, textAlign: "left" }}>Acción</th>
                    <th>Tipo</th><th>Origen</th><th>Prioridad</th>
                  </tr>
                </thead>
                <tbody>
                  {actions.map((a, i) => (
                    <tr key={`ai-${i}`} style={{ borderTop: "1px solid var(--border-2)" }}>
                      <td style={{ padding: 6 }}>{a.action}</td>
                      <td style={{ padding: 6 }}>{a.type || "—"}</td>
                      <td style={{ padding: 6, fontSize: 10 }}>
                        {(a.origin_methodologies || []).join(", ")}
                      </td>
                      <td style={{ padding: 6 }}>{a.priority || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex gap-2 justify-end">
            <button className="btn" onClick={downloadDocx} data-testid="psc-crossval-docx">
              <Download size={11}/> Exportar a Word
            </button>
            <button className="btn" onClick={downloadPdf} data-testid="psc-crossval-pdf">
              <Download size={11}/> Exportar a PDF
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
